"use client";

import Image, { getImageProps } from "next/image";
import { KeyboardEvent, ReactNode, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import PlusThin from "@/icons/actions/plus-thin.svg";
import { cn } from "~/lib/utils";
import Overline from "~/componentsDS/Typography/Overline";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { getTextFromNode } from "~/utils/getTextFromNode";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";

type HormoneInfo = {
    subtitle: string;
    title: string;
    badges: string[];
    titleAdditional: string;
    description: string[];
};

export type Hormone = {
    title: ReactNode;
    description: ReactNode;
    imageUrl: string;
    mobileImageUrl?: string;
    imageAlt?: string;
    mobileDescription?: ReactNode;
    info: HormoneInfo;
};

export type HormoneCardEventData = {
    event_name: string;
    widget: string;
    meta_data?: Record<string, unknown>;
};

const defaultEventData: HormoneCardEventData = {
    event_name: GTM_EVENT_NAMES.LP_INTERACTION,
    widget: "our_science",
};

type HormoneCardProps = {
    hormone: Hormone;
    variant?: "default" | "wide" | "compact" | "compact-wide" | "tile";
    titleAs?: "h3" | "h4";
    wideFromLg?: boolean;
    imageAlt?: string;
    eventData?: HormoneCardEventData;
    className?: string;
    surfaceClassName?: string;
    sizes?: string;
    onOpen?: (title: string) => void;
    trackOpen?: boolean;
};

const HormoneCard = ({
    hormone,
    variant = "default",
    titleAs = "h3",
    wideFromLg = false,
    imageAlt,
    eventData = defaultEventData,
    className,
    surfaceClassName,
    sizes,
    onOpen,
    trackOpen = true,
}: HormoneCardProps) => {
    const sendEvent = useTrackEvent();
    const [isOpened, setIsOpened] = useState(false);

    const { title, description, imageUrl, mobileImageUrl, mobileDescription, info } = hormone;
    const resolvedImageAlt = imageAlt ?? hormone.imageAlt ?? "";
    const resolvedMobileImageUrl = mobileImageUrl ?? imageUrl;
    const isCompact = variant === "compact" || variant === "compact-wide";
    const isWide = variant === "wide" || variant === "compact-wide";
    const isTile = variant === "tile";
    const hasResponsiveWideLayout = wideFromLg && !isWide;

    const handleToggle = () => {
        if (!isOpened) {
            const clickText = getTextFromNode(hormone.title);
            if (onOpen) {
                onOpen(clickText);
            } else if (trackOpen) {
                sendEvent({
                    event: "user_interaction",
                    value: {
                        event_name: eventData.event_name,
                        widget: eventData.widget,
                        meta_data: {
                            ...eventData.meta_data,
                            click_text: clickText,
                        },
                    },
                });
            }
        }

        setIsOpened(prev => !prev);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
        }
    };

    const imageWrapperClassName = isCompact
        ? undefined
        : cn("absolute duration-100", isOpened ? "inset-3 delay-100" : "inset-0");
    const imageSizes = sizes ?? (isWide ? "100vw" : "(max-width: 767px) 100vw, 50vw");
    const {
        props: desktopImageProps,
    } = getImageProps({
        src: imageUrl,
        alt: resolvedImageAlt,
        fill: true,
        sizes: imageSizes,
    });
    const responsiveImage = (
        <picture>
            <source
                media="(min-width: 768px)"
                srcSet={desktopImageProps.srcSet}
                sizes={desktopImageProps.sizes}
            />
            <Image
                src={resolvedMobileImageUrl}
                alt={resolvedImageAlt}
                fill
                sizes={imageSizes}
                loading="lazy"
                className={cn(
                    "rounded-2xl object-cover object-center transition-transform duration-500 ease-out",
                    !isOpened && "group-hover:scale-110"
                )}
            />
        </picture>
    );

    return (
        <div
            role="button"
            tabIndex={0}
            aria-expanded={isOpened}
            className={cn(
                "relative flex overflow-hidden rounded-2xl cursor-pointer group select-none",
                isCompact
                    ? cn("items-end", isWide ? "h-[245px]" : "h-full min-h-[245px]")
                    : cn(
                        isTile && "min-h-[200px]",
                        !isTile && (isWide ? "min-h-[260px]" : "h-[520px]"),
                        hasResponsiveWideLayout && "lg:h-auto lg:min-h-[260px]"
                    ),
                className
            )}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
        >
            {surfaceClassName && (
                <div
                    className={cn("pointer-events-none absolute inset-0 rounded-2xl", surfaceClassName)}
                    aria-hidden="true"
                />
            )}

            {isCompact ? (
                responsiveImage
            ) : (
                <div className={imageWrapperClassName}>
                    {responsiveImage}
                </div>
            )}

            <div
                className={cn(
                    "relative z-10 flex w-full flex-col rounded-2xl transition-all duration-300",
                    isCompact ? "px-6 pt-6" : isTile ? "px-4 py-5" : "p-8",
                    !isCompact && "h-full",
                    !isCompact && isWide && "min-h-[260px]",
                    !isCompact && hasResponsiveWideLayout && "lg:min-h-[260px]",
                    isOpened ? "bg-pureWhite/70 text-primary500 backdrop-blur-[25px]" : "bg-pureWhite/0 text-pureWhite"
                )}
            >
                <div className="flex justify-between gap-[6px] w-full">
                    <Heading
                        as={titleAs}
                        size={isTile ? "xs" : "sm"}
                        className="!leading-[1.1] tracking-[-0.01em]"
                    >
                        {title}
                    </Heading>

                    <div
                        className={cn(
                            "flex justify-center items-center w-[56px] h-[56px] shrink-0 text-pureWhite rounded-full border-[2px] backdrop-blur-[10px] transition-all duration-300",
                            isCompact ? "border-pureWhite/25 bg-pureWhite/10" : "border-pureWhite/50 bg-trueBlack/25",
                            isOpened
                                ? isCompact
                                    ? "rotate-45 border-pureWhite/20 bg-trueBlack/30"
                                    : "rotate-45"
                                : "group-hover:bg-primary300"
                        )}
                    >
                        <PlusThin width={28} height={28} />
                    </div>
                </div>

                {isCompact ? (
                    <motion.div
                        animate={{ opacity: isOpened ? 1 : 0 }}
                        transition={{ duration: 0.3 }}
                        className={cn(
                            "flex mt-auto pt-[30px] pb-8",
                            "transition-[opacity,visibility] duration-300",
                            !isOpened && "invisible pointer-events-none",
                            isWide ? "flex-row gap-6" : "flex-col space-y-[6px]"
                        )}
                    >
                        <div>
                            <Paragraph className="mb-[3px] text-gray400">{info.subtitle}</Paragraph>
                            <Paragraph size="xl" sizes={{ lg: "2xl" }} weight="500" className="mb-3 leading-[1.2]">{info.title}</Paragraph>
                            <ul className="mb-[10px] flex flex-wrap gap-2 border-b border-gray300 pb-[15px]">
                                {info.badges.map((item) => (
                                    <li key={item}>
                                        <Overline
                                            size="xs"
                                            className="inline-flex items-center h-[30px] text-pureWhite bg-[#2A3966] p-2 rounded-full"
                                        >
                                            {item}
                                        </Overline>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <Paragraph size="lg" weight="500" className="mb-2">{info.titleAdditional}</Paragraph>
                            <ul className="flex flex-col gap-3">
                                {info.description.map((item, index) => (
                                    <li key={`desc-${index}`} className="relative pl-5">
                                        <div className="absolute top-[8px] left-2 w-[4px] h-[4px] rounded-full bg-primary500" />
                                        <Paragraph>{item}</Paragraph>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                ) : (
                    <AnimatePresence>
                        {isOpened && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className={cn(
                                    "flex overflow-hidden mt-[6px]",
                                    isWide
                                        ? "flex-row gap-6"
                                        : cn(
                                            "flex-col space-y-[6px]",
                                            hasResponsiveWideLayout && "lg:flex-row lg:gap-6 lg:space-y-0"
                                        )
                                )}
                            >
                                <div className="space-y-[6px]">
                                    <Paragraph className="text-gray400 mb-[5px]">{info.subtitle}</Paragraph>
                                    <Paragraph size="xl" weight="500" className="leading-[1.2] mb-3">{info.title}</Paragraph>
                                    <ul className="flex flex-wrap gap-1 pb-[15px] mb-4 border-b border-gray300">
                                        {info.badges.map((item) => (
                                            <li key={item}>
                                                <Overline
                                                    size="xs"
                                                    className="inline-flex items-center h-[30px] text-pureWhite bg-[#2A3966] px-2 pt-[2px] rounded-full"
                                                >
                                                    {item}
                                                </Overline>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="space-y-[6px]">
                                    <Paragraph size="xl" weight="500" className="mb-2">{info.titleAdditional}</Paragraph>
                                    <ul className="flex flex-col gap-2">
                                        {info.description.map((item, index) => (
                                            <li key={`desc-${index}`} className="relative pl-6">
                                                <div className="absolute top-[6px] left-2 w-[5px] h-[5px] rounded-full bg-primary500"></div>
                                                <Paragraph>{item}</Paragraph>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                )}
            </div>

            <div className={cn(
                "absolute bottom-0 left-0 right-0 z-10 transition-all duration-300",
                isCompact ? "p-6 pb-8" : isTile ? "px-4 pb-5" : "pb-8 px-8",
                isOpened && "opacity-0 invisible pointer-events-none"
            )}>
                <Paragraph size={isTile ? "lg" : "xl"} weight="500" className={cn("text-pureWhite", isCompact && "hidden md:block")}>{description}</Paragraph>
                {isCompact && (
                    <Paragraph size="xl" weight="400" className="text-pureWhite md:hidden">{mobileDescription ?? description}</Paragraph>
                )}
            </div>
        </div>
    );
};

export default HormoneCard;
